/**
 * viewportHeightSync.ts — Extracted helper that keeps the
 * `--messages-viewport-height` CSS custom property in sync with the
 * actual visible message-list area.
 *
 * Responsibilities:
 * - Compute the visible height as:
 *     body.offsetHeight - header.offsetHeight - composer.offsetHeight
 *     - chatParticipantsGap - messagesEdgeGap
 * - Push the computed value into `list.setViewportHeight(height)`.
 * - Push the current header/composer heights into `list.setViewportInsets()`
 *   so the history list gains top/bottom padding that keeps content visible
 *   below the overlaid header and composer.
 * - Observe body, header, and composer with ResizeObserver so that
 *   any size change (window resize, composer growth, header change)
 *   triggers a recalculation.
 * - Fall back to a `window.resize` listener when ResizeObserver is
 *   unavailable.
 * - Return a dispose function that cleans up all observers/listeners.
 *
 * Why body-based:
 *   Reading `.messages.offsetHeight` directly creates an infinite
 *   feedback loop because the turn's min-height grows `.messages`,
 *   which would grow the reading. `document.body` is pinned to
 *   viewport size via `html, body { height: 100% }` in theme.css.
 *
 * Exports:
 * - installViewportHeightSync(elements) — Sets up observation and
 *   returns a cleanup function.
 * - ViewportSyncElements — The required element bag type.
 */

import { logger } from "./logger.js";
import type { MessageHistoryComponent } from "./components/MessageHistoryComponent.js";

/**
 * Required DOM elements for viewport height synchronization.
 * All elements must be present; missing elements should be
 * validated before calling installViewportHeightSync.
 */
export type ViewportSyncElements = {
    body: HTMLElement;
    header: HTMLElement;
    composer: HTMLElement;
    messages: HTMLElement;
    list: MessageHistoryComponent;
};

/**
 * Compute the visible message-list viewport height by subtracting
 * the header, composer, one --chat-participants-gap, and the shared
 * --messages-edge-gap token from the body height.
 *
 * All heights use offsetHeight (includes borders) for accuracy.
 * The participant gap is read via getComputedStyle for robustness —
 * the calculation auto-adapts when CSS values change.
 */
function computeViewportHeight(elements: ViewportSyncElements): number {
    const bodyHeight = elements.body.offsetHeight;
    const headerHeight = elements.header.offsetHeight;
    const composerHeight = elements.composer.offsetHeight;
    const computedStyles = window.getComputedStyle(elements.list);
    const chatParticipantsGap =
        parseFloat(computedStyles.getPropertyValue("--chat-participants-gap")) || 0;
    const messagesEdgeGap =
        parseFloat(computedStyles.getPropertyValue("--messages-edge-gap")) || 0;

    return bodyHeight - headerHeight - composerHeight - chatParticipantsGap - messagesEdgeGap;
}

/**
 * Install viewport height synchronization on the given elements.
 *
 * Observes body, header, and composer with ResizeObserver (or falls
 * back to window.resize) and pushes recalculated heights into
 * `list.setViewportHeight()`.
 *
 * @param elements - The required DOM elements for measurement.
 * @returns A dispose function that removes all observers and listeners.
 */
export function installViewportHeightSync(elements: ViewportSyncElements): () => void {
    // Fail fast: validate that all required layout elements are present.
    // A missing element would silently produce wrong turn spacing instead
    // of surfacing a clear error during development.
    const requiredEntries: [string, unknown][] = [
        ["body", elements.body],
        ["header", elements.header],
        ["composer", elements.composer],
        ["messages", elements.messages],
        ["list", elements.list],
    ];
    for (const [name, el] of requiredEntries) {
        if (!(el instanceof HTMLElement)) {
            throw new Error(
                `installViewportHeightSync: required element '${name}' is missing or not an HTMLElement`
            );
        }
    }

    const syncViewportHeight = (): void => {
        const headerHeight = elements.header.offsetHeight;
        const composerHeight = elements.composer.offsetHeight;
        const height = computeViewportHeight(elements);
        elements.list.setViewportHeight(height);
        elements.list.setViewportInsets(headerHeight, composerHeight);
        logger.debug("syncViewportHeight: updated", {
            height,
            bodyHeight: elements.body.offsetHeight,
            headerHeight,
            composerHeight,
        });
    };

    // Use ResizeObserver on body, header, and composer to react to any
    // size change (window resize, mobile virtual-keyboard, orientation
    // change, composer growth/shrink, header change).
    if (typeof ResizeObserver !== "undefined") {
        const resizeObserver = new ResizeObserver(() => {
            syncViewportHeight();
        });
        resizeObserver.observe(elements.body);
        resizeObserver.observe(elements.header);
        resizeObserver.observe(elements.composer);
        logger.debug("syncViewportHeight: ResizeObserver attached to body, header, composer");

        // Initial sync so the first turn gets the correct min-height
        syncViewportHeight();

        // Return dispose function that disconnects the observer
        return () => {
            resizeObserver.disconnect();
        };
    }

    // Fallback: listen to window resize events for older environments
    window.addEventListener("resize", syncViewportHeight);
    logger.debug("syncViewportHeight: window resize fallback attached");

    // Initial sync so the first turn gets the correct min-height
    syncViewportHeight();

    // Return dispose function that removes the listener
    return () => {
        window.removeEventListener("resize", syncViewportHeight);
    };
}
